import { useState } from 'react';
import {
  Title,
  Button,
  Table,
  Image,
  Text,
  Stack,
  Group,
  Paper,
  Card,
  TextInput,
  Textarea,
  Modal,
  Badge,
  Loader,
  Center,
  Pagination,
  ThemeIcon,
  ActionIcon,
  UnstyledButton,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconStar, IconTrash, IconSearch, IconCheck, IconPackage } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { StarRating } from '../../components/reviews';
import api from '../../services/api';

interface Review {
  id: string;
  productId: string;
  product?: {
    id: string;
    name: string;
    brand?: string;
    imageUrl?: string;
  };
  user?: {
    id: string;
    username: string;
  };
  rating: number;
  title: string;
  content: string;
  commentCount: number;
  createdAt: string;
}

interface Product {
  id: string;
  barcode: string;
  name: string;
  brand?: string;
  imageUrl?: string;
}

interface PaginatedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export default function AdminReviews() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);

  // Product search
  const [products, setProducts] = useState<Product[]>([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [productSearch, setProductSearch] = useState('');

  const form = useForm({
    initialValues: {
      rating: 4,
      content: '',
    },
    validate: {
      content: (value) => (value.trim().length > 0 ? null : 'Content is required'),
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ['admin-reviews', page],
    queryFn: () =>
      api.get<PaginatedResult<Review>>('/admin/reviews', { params: { page, pageSize: 20 } }).then((r) => r.data),
    staleTime: 30_000,
  });

  const reviews = data?.items ?? [];
  const pagination = {
    current: data?.page ?? page,
    pageSize: data?.pageSize ?? 20,
    total: data?.totalCount ?? 0,
    totalPages: data?.totalPages ?? 1,
  };

  const addReviewMutation = useMutation({
    mutationFn: (values: { productId: string; rating: number; content: string }) =>
      api.post('/reviews', values),
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'Review added!',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      setModalOpen(false);
      form.reset();
      setSelectedProduct(null);
      setProducts([]);
      setProductSearch('');
      queryClient.invalidateQueries({ queryKey: ['admin-reviews'] });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.message || 'Failed to add review',
        color: 'red',
      });
    },
  });

  const deleteReviewMutation = useMutation({
    mutationFn: (reviewId: string) => api.delete(`/admin/reviews/${reviewId}`),
    onSuccess: () => {
      notifications.show({
        title: 'Deleted',
        message: 'Review has been deleted',
        color: 'green',
      });
      queryClient.invalidateQueries({ queryKey: ['admin-reviews'] });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete review',
        color: 'red',
      });
    },
  });

  const searchProducts = async (query: string) => {
    if (!query.trim()) {
      setProducts([]);
      return;
    }

    setSearchingProducts(true);
    try {
      const response = await api.get<{ items: Product[] }>('/products/search', {
        params: { query, pageSize: 10 },
      });
      setProducts(response.data.items || []);
    } catch (error) {
      setProducts([]);
    } finally {
      setSearchingProducts(false);
    }
  };

  const handleAddReview = async (values: typeof form.values) => {
    if (!selectedProduct) {
      notifications.show({
        title: 'Select Product',
        message: 'Please select a product first',
        color: 'yellow',
      });
      return;
    }

    addReviewMutation.mutate({
      productId: selectedProduct.id,
      rating: values.rating,
      content: values.content,
    });
  };

  const handleDeleteReview = (review: Review) => {
    modals.openConfirmModal({
      title: 'Delete Review',
      children: (
        <Text size="sm">
          Are you sure you want to delete this review?
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteReviewMutation.mutate(review.id),
    });
  };

  const openModal = () => {
    form.reset();
    setSelectedProduct(null);
    setProducts([]);
    setProductSearch('');
    setModalOpen(true);
  };

  const rows = reviews.map((review) => (
    <Table.Tr key={review.id}>
      <Table.Td>
        <Group gap="sm">
          {review.product?.imageUrl ? (
            <Image src={review.product.imageUrl} w={40} h={40} radius="sm" fit="contain" />
          ) : (
            <ThemeIcon size={40} radius="sm" variant="light" color="gray">
              <IconPackage size={20} />
            </ThemeIcon>
          )}
          <div>
            <Text size="sm" fw={500} lineClamp={1}>
              {review.product?.name || 'Unknown'}
            </Text>
            <Text size="xs" c="dimmed">
              {review.product?.brand}
            </Text>
          </div>
        </Group>
      </Table.Td>
      <Table.Td>
        <StarRating rating={review.rating} size="xs" />
      </Table.Td>
      <Table.Td>
        <Text size="sm" lineClamp={1}>
          {review.content}
        </Text>
      </Table.Td>
      <Table.Td>
        <Badge variant="light">
          {review.user?.username || 'Admin'}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          <Badge variant="light" color="blue" size="sm">💬 {review.commentCount}</Badge>
        </Group>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {new Date(review.createdAt).toLocaleDateString()}
        </Text>
      </Table.Td>
      <Table.Td>
        <ActionIcon
          variant="subtle"
          color="red"
          onClick={() => handleDeleteReview(review)}
        >
          <IconTrash size={16} />
        </ActionIcon>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>Reviews</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={openModal}>
          Add Review
        </Button>
      </Group>

      <Card shadow="sm" padding="lg" radius="md" withBorder w={200}>
        <Group>
          <ThemeIcon size={48} radius="md" variant="light" color="yellow">
            <IconStar size={24} />
          </ThemeIcon>
          <div>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
              Total Reviews
            </Text>
            <Text size="xl" fw={700}>
              {pagination.total.toLocaleString()}
            </Text>
          </div>
        </Group>
      </Card>

      <Paper shadow="xs" radius="md" withBorder>
        {isLoading ? (
          <Center py="xl">
            <Loader size="lg" />
          </Center>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={250}>Product</Table.Th>
                <Table.Th w={120}>Rating</Table.Th>
                <Table.Th>Title</Table.Th>
                <Table.Th w={120}>Author</Table.Th>
                <Table.Th w={130}>Engagement</Table.Th>
                <Table.Th w={100}>Date</Table.Th>
                <Table.Th w={50}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        )}
      </Paper>

      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          Total {pagination.total} reviews
        </Text>
        <Pagination
          value={pagination.current}
          onChange={(p) => setPage(p)}
          total={pagination.totalPages}
        />
      </Group>

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Add Review"
        size="lg"
      >
        <form onSubmit={form.onSubmit(handleAddReview)}>
          <Stack gap="md">
            {/* Product Search */}
            <div>
              <Text size="sm" fw={500} mb={4}>Product</Text>
              <TextInput
                placeholder="Search products by name..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    searchProducts(productSearch);
                  }
                }}
                rightSection={
                  <ActionIcon
                    variant="subtle"
                    onClick={() => searchProducts(productSearch)}
                    loading={searchingProducts}
                  >
                    <IconSearch size={16} />
                  </ActionIcon>
                }
              />

              {products.length > 0 && (
                <Paper withBorder mt="xs" mah={200} style={{ overflow: 'auto' }}>
                  {products.map((product) => (
                    <UnstyledButton
                      key={product.id}
                      onClick={() => {
                        setSelectedProduct(product);
                        setProducts([]);
                        setProductSearch('');
                      }}
                      p="sm"
                      w="100%"
                      style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}
                    >
                      <Group>
                        {product.imageUrl && (
                          <Image src={product.imageUrl} w={30} h={30} radius="sm" fit="contain" />
                        )}
                        <div>
                          <Text size="sm" fw={500}>{product.name}</Text>
                          <Text size="xs" c="dimmed">{product.brand}</Text>
                        </div>
                      </Group>
                    </UnstyledButton>
                  ))}
                </Paper>
              )}

              {selectedProduct && (
                <Card withBorder mt="xs" p="sm">
                  <Group>
                    {selectedProduct.imageUrl && (
                      <Image src={selectedProduct.imageUrl} w={50} h={50} radius="sm" fit="contain" />
                    )}
                    <div>
                      <Text size="sm" fw={500}>{selectedProduct.name}</Text>
                      <Text size="xs" c="dimmed">{selectedProduct.brand}</Text>
                    </div>
                  </Group>
                </Card>
              )}
            </div>

            <div>
              <Text size="sm" fw={500} mb={4}>Rating</Text>
              <StarRating
                rating={form.values.rating}
                readonly={false}
                onChange={(rating) => form.setFieldValue('rating', rating)}
                size="lg"
              />
            </div>

            <Textarea
              label="Review Content"
              placeholder="Share your thoughts about this product..."
              minRows={4}
              maxLength={2000}
              {...form.getInputProps('content')}
            />

            <Group justify="flex-end">
              <Button variant="default" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={addReviewMutation.isPending}>
                Post Review
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
